import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { ShoppingCart, Eye } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import SideMenu from '../components/categories/SideMenu';
import { Link } from 'react-router-dom';

const PageWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const MainContent = styled.main`
  width: 100%;

  @media (min-width: 768px) {
    width: 75%;
    padding-left: 40px;
  }
`;

const SidebarWrapper = styled.aside`
  width: 100%;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    width: 25%;
    position: sticky;
    top: 20px;
  }
`;

const Header = styled.div`
  margin-bottom: 30px;
  h1 {
    font-size: 32px;
    color: #FF6347;
    margin-bottom: 10px;
  }
  p {
    font-size: 16px;
    color: #555;
  }
`;

const ProductList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
`;

const ProductCard = styled.div`
  background-color: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
`;

const ProductTitle = styled.h3`
  margin: 10px 0;
  font-size: 16px;
  color: #333;
`;

const OriginalPrice = styled.p`
  font-size: 14px;
  color: #28a745;
  text-decoration: line-through;
  margin-bottom: 5px;
`;

const Price = styled.p`
  font-size: 16px;
  color: #FF6347;
  font-weight: bold;
`;

const ActiveDays = styled.p`
  font-size: 14px;
  color: #333;
  margin-top: 10px;
`;

const MinPurchaseInfo = styled.p`
  font-size: 12px;
  color: #FF6347;
  margin-top: 5px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  gap: 10px;
`;

const UrgencyMessage = styled.p`
  font-size: 14px;
  color: #dc3545;
  font-weight: bold;
  margin-top: 10px;
`;

const AddToCartButton = styled.button`
  background-color: #FF6347;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  width: 48%;
  height: 40px;
  svg {
    margin-right: 5px;
  }

  &:hover {
    background-color: #ff5733;
  }
`;

const ViewProductButton = styled(Link)`
  background-color: #007BFF;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  width: 48%;
  height: 40px;
  svg {
    margin-right: 5px;
  }

  &:hover {
    background-color: #45a049;
  }
`;

const SpecialsPage = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const response = await fetch('/api/specials/');
        const data = await response.json();
        setPromotions(data);
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load promotions!');
        setLoading(false);
      }
    };

    fetchPromotions();
  }, []);

  const handleAddToCart = (product, promotion) => {
    const price = promotion ? promotion.promotional_price : product.price;
    const minPurchaseQuantity = promotion ? promotion.min_purchase_quantity : (product.min_weight || 1);
  
    const itemToAdd = {
      ...product,
      price,
      promotional_price: promotion ? promotion.promotional_price : undefined,
      isPrePacked: product.is_pre_packed,
      quantity: product.is_pre_packed ? minPurchaseQuantity : undefined,
      selectedWeight: product.is_pre_packed ? undefined : minPurchaseQuantity,
      weight_unit: product.weight_unit || 'kg',
      min_purchase_quantity: minPurchaseQuantity,
      min_purchase_unit: promotion ? promotion.min_purchase_unit : product.weight_unit,
    };
  
    if (!product.is_pre_packed && minPurchaseQuantity <= 0) {
      toast.error('Invalid weight input.');
      return;
    }
  
    try {
      addToCart(itemToAdd, product.is_pre_packed ? itemToAdd.quantity : itemToAdd.selectedWeight);
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Error adding item to cart');
    }
  };

  const getActiveDays = (days) => {
    const dayNames = {
      MON: 'Monday',
      TUE: 'Tuesday',
      WED: 'Wednesday',
      THU: 'Thursday',
      FRI: 'Friday',
      SAT: 'Saturday',
      SUN: 'Sunday'
    };
    
    if (days.length === 7) {
      return 'Limited time offer!';
    } else {
      return `This Offer Available on: ${days.map(day => dayNames[day]).join(', ')}`;
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <PageWrapper>
      <ContentWrapper>
        <SidebarWrapper>
          <SideMenu />
        </SidebarWrapper>
        <MainContent>
          <Header>
            <h1>Special Offers</h1>
            <p>Discover the best deals available today</p>
          </Header>
          <ProductList>
            {promotions.length > 0 ? (
              promotions.map((promotion) => (
                <ProductCard key={promotion.name}>
                  <ProductTitle>{promotion.name}</ProductTitle>
                  {promotion.products.map((product) => (
                    <div key={product.id}>
                      <ProductImage src={product.image} alt={product.name} />
                      <ProductTitle>{product.name}</ProductTitle>
                      <OriginalPrice>${parseFloat(product.price).toFixed(2)}</OriginalPrice>
                      <Price>${parseFloat(promotion.promotional_price).toFixed(2)}</Price>
                      {promotion.min_purchase_quantity && (
                        <MinPurchaseInfo>
                          Minimum purchase: {promotion.min_purchase_quantity} {promotion.min_purchase_unit}
                        </MinPurchaseInfo>
                      )}
                      {promotion.active_days.length === 7 ? (
                        <UrgencyMessage>{getActiveDays(promotion.active_days)}</UrgencyMessage>
                      ) : (
                        <ActiveDays>{getActiveDays(promotion.active_days)}</ActiveDays>
                      )}
                      <ButtonGroup>
                        <AddToCartButton onClick={() => handleAddToCart(product, promotion)}>
                          <ShoppingCart size={16} />
                          + Cart
                        </AddToCartButton>
                        <ViewProductButton to={`/product/${product.slug}`}>
                          <Eye size={16} />
                          View
                        </ViewProductButton>
                      </ButtonGroup>
                    </div>
                  ))}
                </ProductCard>
              ))
            ) : (
              <p>No specials available.</p>
            )}
          </ProductList>
        </MainContent>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default SpecialsPage;